/**
 * Solo winner podium for Level 10 (Double-Spinner Survival).
 * One dancer centered, spotlight + fireworks, then a sparkling crown
 * descends from above and settles on their head. Banner reads
 * "<NAME>  ·  WINNER" beneath.
 *
 * Loops forever (RAF-driven sin animations + crown idle bob once seated).
 */

import { useEffect, useRef } from "react";
import * as THREE from "three";
import type { AvatarConfig } from "@shared/types";
import { buildRunnerCharacter } from "../game/engine/runnerCharacter";

interface Props {
  avatar: AvatarConfig;
  displayName: string;
}

export function WinnerPodiumScene({ avatar, displayName }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.25;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(46, 1, 0.1, 80);
    camera.position.set(0, 3.0, 5.4);
    camera.lookAt(0, 2.2, 0);

    scene.add(new THREE.AmbientLight(0xffffff, 1.1));
    const sun = new THREE.DirectionalLight(0xffffff, 1.7);
    sun.position.set(4, 9, 6); sun.castShadow = true;
    scene.add(sun);
    const spot = new THREE.SpotLight(0xfff0c0, 4.5, 30, Math.PI / 5, 0.4, 1);
    spot.position.set(0, 9, 3); spot.target.position.set(0, 2, 0);
    scene.add(spot, spot.target);

    // Center podium step.
    const stepMat = new THREE.MeshStandardMaterial({ color: 0xffd166, roughness: 0.4, metalness: 0.12 });
    const step = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 1.7, 1.3, 36), stepMat);
    step.position.y = 0.65; step.castShadow = true; step.receiveShadow = true;
    scene.add(step);
    const rim = new THREE.Mesh(
      new THREE.TorusGeometry(1.55, 0.08, 12, 64),
      new THREE.MeshStandardMaterial({ color: 0xff5fa7, metalness: 0.35, roughness: 0.4 }),
    );
    rim.rotation.x = Math.PI / 2; rim.position.y = 1.3;
    scene.add(rim);

    // Winner runner.
    const dancerParts = buildRunnerCharacter(avatar);
    const dancer = new THREE.Group();
    dancer.add(dancerParts.group);
    dancerParts.group.scale.setScalar(0.6);
    dancer.position.set(0, 1.3, 0);
    scene.add(dancer);

    // Crown — descends + spins, then sits on the head.
    const crownGroup = new THREE.Group();
    const goldMat = new THREE.MeshStandardMaterial({
      color: 0xffd166, emissive: 0xffb020, emissiveIntensity: 0.45,
      metalness: 0.85, roughness: 0.18,
    });
    const band = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.36, 0.16, 24), goldMat);
    crownGroup.add(band);
    for (let i = 0; i < 8; i++) {
      const ang = (i / 8) * Math.PI * 2;
      const spike = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.24, 4), goldMat);
      spike.position.set(Math.cos(ang) * 0.32, 0.16, Math.sin(ang) * 0.32);
      crownGroup.add(spike);
      const jewel = new THREE.Mesh(
        new THREE.SphereGeometry(0.04, 10, 8),
        new THREE.MeshStandardMaterial({ color: i % 2 ? 0xff2ca4 : 0x6bd0ff, emissive: i % 2 ? 0xff2ca4 : 0x6bd0ff, emissiveIntensity: 0.35 }),
      );
      jewel.position.set(Math.cos(ang) * 0.32, 0.04, Math.sin(ang) * 0.32);
      crownGroup.add(jewel);
    }
    crownGroup.position.set(0, 9, 0);
    scene.add(crownGroup);

    // Sparkle particles trailing the crown as it descends.
    const sparkles: THREE.Mesh[] = [];
    const sparkleMat = new THREE.MeshBasicMaterial({ color: 0xffffaa, transparent: true });
    for (let i = 0; i < 24; i++) {
      const m = new THREE.Mesh(new THREE.SphereGeometry(0.05, 6, 5), sparkleMat.clone());
      m.position.set((Math.random() - 0.5) * 0.6, 0, (Math.random() - 0.5) * 0.6);
      scene.add(m); sparkles.push(m);
    }

    // Fireworks (confetti pieces drifting upward).
    const confetti: THREE.Mesh[] = [];
    const colors = [0xff2ca4, 0xffd93d, 0x28d7ff, 0x5cff74, 0xff8b3d, 0xffffff];
    for (let i = 0; i < 36; i++) {
      const piece = new THREE.Mesh(
        new THREE.BoxGeometry(0.08, 0.045, 0.13),
        new THREE.MeshBasicMaterial({ color: colors[i % colors.length] }),
      );
      piece.position.set((Math.random() - 0.5) * 6, 0.6 + Math.random() * 4, -1 + (Math.random() - 0.5) * 3);
      scene.add(piece); confetti.push(piece);
    }

    const resize = () => {
      const w = canvas.clientWidth, h = canvas.clientHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / Math.max(1, h);
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener("resize", resize);

    const start = performance.now();
    let raf = 0;
    const CROWN_FALL_DURATION_S = 2.5;
    const CROWN_DELAY_S = 1.0;
    const CROWN_REST_Y = 2.95; // head crown sits roughly here for the 0.6-scale runner
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const t = (performance.now() - start) * 0.001;

      // Dancer celebration loop (mirrors BattlePodiumScene's logic).
      const bounce = Math.abs(Math.sin(t * 3.4)) * 0.45;
      const sway = Math.sin(t * 3.4);
      const cheerPhase = (t * 0.45) % 1;
      const cheerHop = cheerPhase < 0.35 ? Math.sin(cheerPhase / 0.35 * Math.PI) * 0.55 : 0;
      const body = dancerParts.body;
      body.position.y = bounce + cheerHop;
      body.rotation.y = Math.sin(t * 2.1) * 0.45;
      body.rotation.z = sway * 0.2;
      dancerParts.leftLeg.rotation.x = -sway * 0.55 - cheerHop * 0.6;
      dancerParts.rightLeg.rotation.x = sway * 0.55 - cheerHop * 0.6;

      // Crown descent — eased linear → settle.
      const crownT = Math.max(0, Math.min(1, (t - CROWN_DELAY_S) / CROWN_FALL_DURATION_S));
      // Smoothstep ease.
      const e = crownT < 1 ? crownT * crownT * (3 - 2 * crownT) : 1;
      crownGroup.position.y = THREE.MathUtils.lerp(9, CROWN_REST_Y, e);
      // Slow spin descending, idle bob after settling.
      if (crownT < 1) {
        crownGroup.rotation.y += 0.06;
      } else {
        crownGroup.position.y = CROWN_REST_Y + Math.sin(t * 2) * 0.04;
        crownGroup.rotation.y += 0.012;
      }

      // Sparkles around / under the crown.
      sparkles.forEach((m, i) => {
        const sa = t * (1 + i * 0.07) + i;
        m.position.x = Math.cos(sa) * (0.5 + (i % 5) * 0.06);
        m.position.z = Math.sin(sa) * (0.5 + (i % 5) * 0.06);
        m.position.y = crownGroup.position.y + Math.sin(sa * 1.4 + i) * 0.25;
        (m.material as THREE.MeshBasicMaterial).opacity = 0.55 + Math.sin(sa * 2.3) * 0.4;
      });

      // Confetti rising + recycling.
      confetti.forEach((piece, i) => {
        piece.position.y += 0.014 + (i % 4) * 0.003;
        piece.rotation.x += 0.07;
        piece.rotation.y += 0.05;
        if (piece.position.y > 6.5) piece.position.y = 0.5;
      });

      // Slow orbit so the camera shows off the crown.
      camera.position.x = Math.sin(t * 0.18) * 0.6;
      camera.position.z = 5.4 + Math.cos(t * 0.18) * 0.3;
      camera.lookAt(0, 2.4, 0);
      renderer.render(scene, camera);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      renderer.dispose();
      scene.traverse((o) => {
        if (o instanceof THREE.Mesh) {
          o.geometry.dispose();
          const m = o.material;
          if (Array.isArray(m)) m.forEach((mm) => mm.dispose());
          else m.dispose();
        }
      });
    };
  }, [avatar, displayName]);

  return <canvas ref={ref} className="winner-podium-canvas" />;
}
